import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Truck, CheckCircle, XCircle, Clock, Loader2, Mail, Phone, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");
  const [paymentData, setPaymentData] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const maxPolls = 5;

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (pollCount >= maxPolls) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      setPaymentData(response.data);
      
      if (response.data.payment_status === "paid") {
        setStatus("success");
        return;
      } else if (response.data.status === "expired") {
        setStatus("expired");
        return;
      }
      
      // Continue polling
      setPollCount(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
      
    } catch (error) {
      console.error("Error checking payment status:", error);
      setStatus("error");
    }
  };

  const renderContent = () => {
    switch (status) {
      case "checking":
        return (
          <div className="text-center">
            <Loader2 className="h-16 w-16 animate-spin text-orange-500 mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
              Verifying Payment...
            </h1>
            <p className="text-slate-500">Please wait while we confirm your payment.</p>
          </div>
        );
      
      case "success":
        return (
          <div className="text-center animate-fade-in">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-12 w-12 text-green-500" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
              Booking Confirmed!
            </h1>
            <p className="text-slate-500 mb-8 max-w-md mx-auto">
              Thank you for your booking. A confirmation email has been sent to your registered email address.
            </p>
            
            <Card className="bg-slate-50 border-slate-200 mb-8 max-w-md mx-auto">
              <CardContent className="pt-6">
                <h3 className="font-semibold text-slate-900 mb-4">What happens next?</h3>
                <div className="space-y-3 text-left">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-orange-500">1</span>
                    </div>
                    <p className="text-sm text-slate-600">
                      Our team will review your booking and confirm the collection slot within 24 hours.
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-orange-500">2</span>
                    </div>
                    <p className="text-sm text-slate-600">
                      You'll receive a call from our driver on the day of collection.
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-orange-500">3</span>
                    </div>
                    <p className="text-sm text-slate-600">
                      Your items will be delivered safely to the destination.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/">
                <Button 
                  data-testid="new-quote-btn"
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                >
                  Get Another Quote
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        );
      
      case "expired":
        return (
          <div className="text-center animate-fade-in">
            <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
              <Clock className="h-12 w-12 text-amber-500" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
              Session Expired
            </h1>
            <p className="text-slate-500 mb-8">
              Your payment session has expired. Please try again.
            </p>
            <Link to="/">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white">
                Start New Quote
              </Button>
            </Link>
          </div>
        );
      
      case "timeout":
        return (
          <div className="text-center animate-fade-in">
            <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
              <Clock className="h-12 w-12 text-amber-500" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
              Verification Timeout
            </h1>
            <p className="text-slate-500 mb-8 max-w-md mx-auto">
              We couldn't verify your payment status. If you completed the payment, 
              please check your email for confirmation or contact us.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => { setPollCount(0); setStatus("checking"); pollPaymentStatus(); }}
                className="bg-slate-900 hover:bg-slate-800 text-white"
              >
                Try Again
              </Button>
              <Link to="/">
                <Button variant="outline">
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        );
      
      case "error":
      default:
        return (
          <div className="text-center animate-fade-in">
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <XCircle className="h-12 w-12 text-red-500" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
              Something Went Wrong
            </h1>
            <p className="text-slate-500 mb-8 max-w-md mx-auto">
              We encountered an error while verifying your payment. 
              Please contact our support team if you need assistance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/">
                <Button className="bg-orange-500 hover:bg-orange-600 text-white">
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <Truck className="h-8 w-8 text-orange-500" />
              <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
                Transport Anything 247
              </span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Card className="calculator-card">
          <CardContent className="pt-10 pb-10">
            {renderContent()}
          </CardContent>
        </Card>

        {/* Contact Info */}
        <div className="mt-8 text-center">
          <p className="text-slate-500 text-sm mb-4">Need help? Contact us:</p>
          <div className="flex justify-center gap-6">
            <a href="tel:+447757133163" className="flex items-center gap-2 text-slate-600 hover:text-orange-500 transition-colors">
              <Phone className="h-4 w-4" />
              <span className="text-sm">+44 7757 133163</span>
            </a>
            <a href="mailto:customers@trasportanything247.com" className="flex items-center gap-2 text-slate-600 hover:text-orange-500 transition-colors">
              <Mail className="h-4 w-4" />
              <span className="text-sm">Email Us</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
